import React, { useContext, useState } from 'react'
import { useFormikContext } from 'formik'
import { msg } from '@lingui/macro'
import { useLingui } from '@lingui/react'

import Box from '@mui/material/Box'
import Grid from '@mui/material/Grid'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import Step, { StepProps } from '@mui/material/Step'
import StepContent from '@mui/material/StepContent'
import StepLabel from '@mui/material/StepLabel'
import Typography from '@mui/material/Typography'
import PersonRoundedIcon from '@mui/icons-material/PersonRounded'

import CheckboxInput from '@/components/ui/inputs/CheckboxInput'
import PhoneNumberField from '@/components/formik/fields/PhoneNumberField'
import TextField from '@/components/formik/fields/TextField'

import OrderStepCard from '@/components/ui/cards/OrderStepCard'

import { BookingFormContext, BookingFormValues } from './BookingForm'
import Summary from './Summary'
import BottomBar from './BottomBar'

type PatientInfoStepProps = StepProps & {}

const patientInfoMessages = {
  label: msg`Your information`,
  firstName: msg`First name *`,
  lastName: msg`Last name *`,
  email: msg`Email *`,
  phone: msg`Phone *`,
  vitaleCard: msg`I have my Vitale card available *`,
  terms: msg`I accept the terms and conditions of use *`,
  freeTeleconsultationChoice: msg`I confirm my free choice to use teleconsultation *`,
}

export function PatientInfoStep(props: PatientInfoStepProps) {
  const { active, completed, index = 0, last } = props
  const { isMobile, onComplete, onEditStep, onNextStep, onPreviousStep } = useContext(BookingFormContext)
  const { values } = useFormikContext<BookingFormValues>()
  const { i18n } = useLingui()
  const label = i18n._(patientInfoMessages.label)

  const [acceptGCU, setAcceptGCU] = useState(false)
  const [confirmFreeTeleconsultationChoice, setConfirmFreeTeleconsultationChoice] = useState(false)
  const [hasCarteVitale, setHasCarteVitale] = useState(false)

  const handleChangeHasCarteVitale = (event: React.ChangeEvent<HTMLInputElement>) => {
    setHasCarteVitale(event.target.checked)
  }

  const handleChangeAcceptGCU = (event: React.ChangeEvent<HTMLInputElement>) => {
    setAcceptGCU(event.target.checked)
  }

  const handleChangeConfirmFreeTeleconsultationChoice = (event: React.ChangeEvent<HTMLInputElement>) => {
    setConfirmFreeTeleconsultationChoice(event.target.checked)
  }

  if (!active && !completed) {
    return isMobile ? null : (
      <Typography variant="h3" mt={1} mb={1}>
        {label}
      </Typography>
    )
  }

  const { patientInfo } = values
  const isStepDisabled =
    !patientInfo.email ||
    !patientInfo.firstName ||
    !patientInfo.lastName ||
    !patientInfo.phone ||
    !acceptGCU ||
    !confirmFreeTeleconsultationChoice ||
    !hasCarteVitale

  if (isMobile) {
    return (
      <Step {...props}>
        {completed ? null : (
          <StepContent sx={{ height: '100%' }}>
            <Stack height="100%" spacing={2}>
              <Typography variant="h2" mt={1} mb={1}>
                {label}
              </Typography>
              <Box flex={1}>
                <Paper sx={{ borderRadius: '6px', p: '10px 30px 35px' }}>
                  <Grid container spacing={2} sx={{}}>
                    <Grid item xs={12}>
                      <TextField fullWidth label={i18n._(patientInfoMessages.firstName)} name="patientInfo.firstName" />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField fullWidth label={i18n._(patientInfoMessages.lastName)} name="patientInfo.lastName" />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField fullWidth label={i18n._(patientInfoMessages.email)} name="patientInfo.email" />
                    </Grid>
                    <Grid item xs={12}>
                      <PhoneNumberField label={i18n._(patientInfoMessages.phone)} name="patientInfo.phone" />
                    </Grid>
                    <Grid item xs={12}>
                      <Stack>
                        <Box>
                          <CheckboxInput
                            checked={hasCarteVitale}
                            label={i18n._(patientInfoMessages.vitaleCard)}
                            name="carteVitale"
                            onChange={handleChangeHasCarteVitale}
                          />
                        </Box>
                        <Box>
                          <CheckboxInput
                            checked={acceptGCU}
                            label={i18n._(patientInfoMessages.terms)}
                            name="cgu"
                            onChange={handleChangeAcceptGCU}
                          />
                        </Box>
                        <Box>
                          <CheckboxInput
                            checked={confirmFreeTeleconsultationChoice}
                            label={i18n._(patientInfoMessages.freeTeleconsultationChoice)}
                            name="freeTeleconsultationChoice"
                            onChange={handleChangeConfirmFreeTeleconsultationChoice}
                          />
                        </Box>
                      </Stack>
                    </Grid>
                  </Grid>
                </Paper>
              </Box>
              <BottomBar
                disabled={isStepDisabled}
                isFirst={index === 0}
                isLast={last}
                onComplete={onComplete}
                onNext={onNextStep}
                onPrevious={onPreviousStep}
              />
            </Stack>
          </StepContent>
        )}
      </Step>
    )
  }

  return (
    <Step {...props}>
      {completed ? (
        <StepLabel icon={<PersonRoundedIcon />}>
          <Summary
            label={label}
            onEdit={() => onEditStep(index)}
            text={`${patientInfo.firstName} ${patientInfo.lastName}`}
          />
        </StepLabel>
      ) : (
        <StepContent>
          <OrderStepCard
            disabled={isStepDisabled}
            isFirst={index === 0}
            isLast={last}
            title={label}
            onComplete={onComplete}
            onNext={onNextStep}
            onPrevious={onPreviousStep}
          >
            <Grid container spacing={2} sx={{ p: '10px 30px 35px' }}>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label={i18n._(patientInfoMessages.firstName)} name="patientInfo.firstName" />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label={i18n._(patientInfoMessages.lastName)} name="patientInfo.lastName" />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label={i18n._(patientInfoMessages.email)} name="patientInfo.email" />
              </Grid>
              <Grid item xs={12} sm={6}>
                <PhoneNumberField label={i18n._(patientInfoMessages.phone)} name="patientInfo.phone" />
              </Grid>
              <Grid item xs={12}>
                <Stack>
                  <Box>
                    <CheckboxInput
                      checked={hasCarteVitale}
                      label={i18n._(patientInfoMessages.vitaleCard)}
                      name="carteVitale"
                      onChange={handleChangeHasCarteVitale}
                    />
                  </Box>
                  <Box>
                    <CheckboxInput
                      checked={acceptGCU}
                      label={i18n._(patientInfoMessages.terms)}
                      name="cgu"
                      onChange={handleChangeAcceptGCU}
                    />
                  </Box>
                  <Box>
                    <CheckboxInput
                      checked={confirmFreeTeleconsultationChoice}
                      label={i18n._(patientInfoMessages.freeTeleconsultationChoice)}
                      name="freeTeleconsultationChoice"
                      onChange={handleChangeConfirmFreeTeleconsultationChoice}
                    />
                  </Box>
                </Stack>
              </Grid>
            </Grid>
          </OrderStepCard>
        </StepContent>
      )}
    </Step>
  )
}

export default PatientInfoStep
